import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';

import { DetectedDroneListItem, ObserverListItem } from '../../models/entity-list-item.model';
import { ObserverPhotoViewModel } from '../../models/observer-photo.model';
import { ObserverMapFacadeService } from '../../services/observer-map-facade.service';
import { ObserverMapViewportService } from '../../services/observer-map-viewport.service';

interface MetadataField {
  label: string;
  key: string;
  type?: 'date' | 'number' | 'percent' | 'list';
}

type EntityListTab = 'observers' | 'drones';
type ObserverSortKey = 'receivedAtUtc' | 'observerId' | 'photo';
type DroneSortKey = 'lastSeenAtUtc' | 'confidence' | 'observerCount';

@Component({
  selector: 'app-observer-entity-list',
  templateUrl: './observer-entity-list.component.html',
  styleUrl: './observer-entity-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObserverEntityListComponent {
  protected readonly facade = inject(ObserverMapFacadeService);
  protected readonly viewport = inject(ObserverMapViewportService);
  protected readonly observers = this.facade.observerListItems;
  protected readonly drones = this.facade.droneListItems;
  protected readonly activeTab = signal<EntityListTab>('observers');
  protected readonly observerSort = signal<ObserverSortKey>('receivedAtUtc');
  protected readonly droneSort = signal<DroneSortKey>('lastSeenAtUtc');
  protected readonly sortedObservers = computed(() => this.sortObservers(this.observers(), this.observerSort()));
  protected readonly sortedDrones = computed(() => this.sortDrones(this.drones(), this.droneSort()));

  // TODO for CLASS/INTERFACE
  protected readonly observerMetadataFields = [
    { label: 'Observation ID', key: 'id' },
    { label: 'Device ID', key: 'deviceId' },
    { label: 'Observer ID', key: 'observerId' },
    { label: 'Received', key: 'receivedAtUtc', type: 'date' },
    { label: 'Client time', key: 'clientTimestampUtc', type: 'date' },
    { label: 'Longitude', key: 'longitude', type: 'number' },
    { label: 'Latitude', key: 'latitude', type: 'number' },
    { label: 'Altitude', key: 'altitude', type: 'number' },
    { label: 'GPS accuracy m', key: 'reportedGpsAccuracyMeters', type: 'number' },
    { label: 'Bearing deg', key: 'bearingDegrees', type: 'number' },
    { label: 'True heading', key: 'trueHeadingDegrees', type: 'number' },
    { label: 'Magnetic heading', key: 'magneticHeadingDegrees', type: 'number' },
    { label: 'Heading accuracy', key: 'headingAccuracy', type: 'number' },
    { label: 'Gyro X', key: 'gyroX', type: 'number' },
    { label: 'Gyro Y', key: 'gyroY', type: 'number' },
    { label: 'Gyro Z', key: 'gyroZ', type: 'number' },
    { label: 'Message type', key: 'messageType' },
    { label: 'Photo ID', key: 'photoId' },
    { label: 'Created', key: 'createdAtUtc', type: 'date' },
    { label: 'Raw payload', key: 'rawPayloadJson' },
  ] satisfies MetadataField[];

  protected readonly droneMetadataFields = [
    { label: 'Drone ID', key: 'droneId' },
    { label: 'Longitude', key: 'longitude', type: 'number' },
    { label: 'Latitude', key: 'latitude', type: 'number' },
    { label: 'Confidence', key: 'confidence', type: 'percent' },
    { label: 'Observer count', key: 'observerCount' },
    { label: 'Ray miss m', key: 'averageRayMissDistanceMeters', type: 'number' },
    { label: 'First seen', key: 'firstSeenAtUtc', type: 'date' },
    { label: 'Last seen', key: 'lastSeenAtUtc', type: 'date' },
    { label: 'Stale', key: 'isStale' },
    { label: 'Source rays', key: 'sourceRayIds', type: 'list' },
    { label: 'Source devices', key: 'sourceDeviceIds', type: 'list' },
    { label: 'Source observers', key: 'sourceObserverIds', type: 'list' },
  ] satisfies MetadataField[];

  protected getValue(source: unknown, key: string): unknown {
    return (source as Record<string, unknown>)[key];
  }

  protected formatMetadataValue(source: unknown, field: MetadataField): string {
    const value = this.getValue(source, field.key);

    if (value === null || value === undefined || value === '') {
      return '-';
    }

    switch (field.type) {
      case 'date':
        return this.formatUtcDate(value);
      case 'list':
        return Array.isArray(value) && value.length ? value.join(', ') : '-';
      case 'number':
        return this.formatNumber(value);
      case 'percent':
        return `${this.formatNumber(value)}%`;
      default:
        return String(value);
    }
  }

  private formatUtcDate(value: unknown): string {
    const date = value instanceof Date ? value : new Date(String(value));

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return `${date.toISOString().replace('T', ' ').slice(0, 19)} UTC`;
  }

  private formatNumber(value: unknown): string {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return String(value);
    }

    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 6,
    }).format(number);
  }

  protected trackObserver(_index: number, item: ObserverListItem): string {
    return item.key;
  }

  protected trackDrone(_index: number, item: DetectedDroneListItem): string {
    return item.drone.droneId;
  }

  protected trackPhoto(_index: number, photo: ObserverPhotoViewModel): string {
    return photo.photoId;
  }

  protected setActiveTab(tab: EntityListTab): void {
    this.activeTab.set(tab);
  }

  protected updateObserverSort(event: Event): void {
    const select = event.target as HTMLSelectElement;

    if (this.isObserverSortKey(select.value)) {
      this.observerSort.set(select.value);
    }
  }

  protected updateDroneSort(event: Event): void {
    const select = event.target as HTMLSelectElement;

    if (this.isDroneSortKey(select.value)) {
      this.droneSort.set(select.value);
    }
  }

  protected zoomToDrone(item: DetectedDroneListItem): void {
    this.facade.selectDroneId(item.drone.droneId);
    this.viewport.zoomToDrone(item.drone);
  }

  private sortObservers(items: ObserverListItem[], sortKey: ObserverSortKey): ObserverListItem[] {
    return [...items].sort((left, right) => {
      switch (sortKey) {
        case 'observerId':
          return left.observer.observerId - right.observer.observerId;
        case 'photo':
          return Number(right.hasPhoto) - Number(left.hasPhoto) || this.compareObserverTime(left, right);
        case 'receivedAtUtc':
          return this.compareObserverTime(left, right);
      }
    });
  }

  private sortDrones(items: DetectedDroneListItem[], sortKey: DroneSortKey): DetectedDroneListItem[] {
    return [...items].sort((left, right) => {
      switch (sortKey) {
        case 'confidence':
          return right.drone.confidence - left.drone.confidence;
        case 'observerCount':
          return right.drone.observerCount - left.drone.observerCount;
        case 'lastSeenAtUtc':
          return right.drone.lastSeenAtUtc.getTime() - left.drone.lastSeenAtUtc.getTime();
      }
    });
  }

  private compareObserverTime(left: ObserverListItem, right: ObserverListItem): number {
    return this.getTime(right.observer.receivedAtUtc) - this.getTime(left.observer.receivedAtUtc);
  }

  private getTime(value: string | Date | null | undefined): number {
    if (!value) {
      return 0;
    }

    const date = value instanceof Date ? value : new Date(value);

    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  }

  private isObserverSortKey(value: string): value is ObserverSortKey {
    return value === 'receivedAtUtc' || value === 'observerId' || value === 'photo';
  }

  private isDroneSortKey(value: string): value is DroneSortKey {
    return value === 'lastSeenAtUtc' || value === 'confidence' || value === 'observerCount';
  }
}
